import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { AlertTriangle, Sparkles, Activity, Shield, Users, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_VERSION = '1.0.0';

const DISCLAIMERS = [
    {
        type: 'medical',
        version: '1.0',
        icon: AlertTriangle,
        iconColor: 'text-rose-500',
        accentBg: 'bg-rose-50 dark:bg-rose-950/40',
        title: 'Important Health Information',
        body: (
            <>
                <p>Autoimmune Pathways is designed for educational, wellness, and organizational purposes only. The app does not provide medical advice, diagnosis, treatment, or emergency services.</p>
                <p>The app is not monitored in real time and should never be used during medical or mental health emergencies.</p>
                <p>Always consult a licensed healthcare professional regarding medical concerns or treatment decisions.</p>
            </>
        ),
        checkboxLabel: 'I understand that Autoimmune Pathways is not a substitute for professional medical care or emergency services.',
        showExitButton: true,
    },
    {
        type: 'ai',
        version: '1.0',
        icon: Sparkles,
        iconColor: 'text-indigo-500',
        accentBg: 'bg-indigo-50 dark:bg-indigo-950/40',
        title: 'AI Feature Acknowledgement',
        body: (
            <>
                <p>Some features within Autoimmune Pathways use artificial intelligence ("AI") to generate educational responses, summaries, trends, and organizational insights.</p>
                <p className="font-medium">AI-generated content:</p>
                <ul className="list-disc list-inside space-y-1">
                    <li>may be inaccurate or incomplete,</li>
                    <li>is not reviewed by medical professionals,</li>
                    <li>and should not be relied upon for diagnosis, treatment, medication changes, or emergencies.</li>
                </ul>
                <p>Always verify important health information with a qualified healthcare provider.</p>
            </>
        ),
        checkboxLabel: 'I understand that AI-generated content is informational only and is not medical advice.',
    },
    {
        type: 'correlation',
        version: '1.0',
        icon: Activity,
        iconColor: 'text-emerald-500',
        accentBg: 'bg-emerald-50 dark:bg-emerald-950/40',
        title: 'Tracking & Correlation Insights',
        body: (
            <>
                <p>Autoimmune Pathways may identify trends, correlations, or patterns based on information you enter into the app.</p>
                <p className="font-medium">These insights:</p>
                <ul className="list-disc list-inside space-y-1">
                    <li>do not establish medical causation,</li>
                    <li>are not diagnostic tools,</li>
                    <li>and should not replace professional medical evaluation or treatment decisions.</li>
                </ul>
            </>
        ),
        checkboxLabel: 'I understand that app-generated correlations and insights are informational only.',
    },
    {
        type: 'privacy',
        version: '1.0',
        icon: Shield,
        iconColor: 'text-sky-500',
        accentBg: 'bg-sky-50 dark:bg-sky-950/40',
        title: 'Data & Privacy Acknowledgement',
        body: (
            <>
                <p>By using Autoimmune Pathways, you understand that information you voluntarily enter into the app may be electronically stored and processed to provide app functionality, tracking tools, AI features, and reports.</p>
                <p>While we use commercially reasonable safeguards to help protect your information, no system can guarantee absolute security.</p>
            </>
        ),
        checkboxLabel: 'I understand and consent to electronic storage and processing of my information as described in the Privacy Policy.',
    },
    {
        type: 'community',
        version: '1.0',
        icon: Users,
        iconColor: 'text-amber-500',
        accentBg: 'bg-amber-50 dark:bg-amber-950/40',
        title: 'Community Guidelines',
        body: (
            <>
                <p>Community discussions within Autoimmune Pathways are not moderated as medical advice and may contain opinions or inaccurate information from other users.</p>
                <p>Always consult qualified healthcare professionals regarding medical decisions.</p>
                <p>Please avoid sharing highly sensitive personal information publicly.</p>
            </>
        ),
        checkboxLabel: 'I understand that community content is user-generated and not medical advice.',
    },
];

export default function DisclaimerGate() {
    const { token, logout } = useAuth();
    const navigate = useNavigate();
    const [pendingTypes, setPendingTypes] = useState(null); // null = loading, [] = none, [...types] = block
    const [currentIndex, setCurrentIndex] = useState(0);
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Fetch status
    const fetchStatus = useCallback(async () => {
        if (!token) {
            setPendingTypes([]);
            return;
        }
        try {
            const { data } = await axios.get(`${API_URL}/api/disclaimers/status`);
            const pending = (data.pending || []).map(p => p.type);
            // Maintain canonical order from DISCLAIMERS array
            const ordered = DISCLAIMERS.filter(d => pending.includes(d.type)).map(d => d.type);
            setPendingTypes(ordered);
            setCurrentIndex(0);
            setAgreed(false);
        } catch (e) {
            console.error('Failed to fetch disclaimer status', e);
            setPendingTypes([]);
        }
    }, [token]);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    if (!token || pendingTypes === null || pendingTypes.length === 0) return null;

    const currentType = pendingTypes[currentIndex];
    const current = DISCLAIMERS.find(d => d.type === currentType);
    if (!current) return null;

    const total = pendingTypes.length;
    const Icon = current.icon;

    const onContinue = async () => {
        if (!agreed || submitting) return;
        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/api/disclaimers/accept`, {
                type: current.type,
                version: current.version,
                device_info: {
                    user_agent: navigator.userAgent,
                    platform: navigator.platform,
                    language: navigator.language,
                    screen: `${window.screen.width}x${window.screen.height}`,
                    app_version: APP_VERSION,
                    accepted_via: 'web',
                },
            });

            if (currentIndex + 1 >= total) {
                // All done
                setPendingTypes([]);
            } else {
                setCurrentIndex(i => i + 1);
                setAgreed(false);
            }
        } catch (e) {
            console.error('Failed to record acceptance', e);
        } finally {
            setSubmitting(false);
        }
    };

    const onExit = () => {
        logout?.();
        navigate('/');
    };

    return (
        <div
            className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="disclaimer-gate"
            role="dialog"
            aria-modal="true"
        >
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className={`${current.accentBg} px-6 py-5 border-b border-slate-200 dark:border-slate-800`}>
                    <div className="flex items-center gap-3 mb-2">
                        <Icon className={`w-7 h-7 ${current.iconColor}`} />
                        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100" data-testid="disclaimer-title">
                            {current.title}
                        </h2>
                    </div>
                    {/* Progress dots */}
                    <div className="flex items-center gap-1.5 mt-3">
                        {pendingTypes.map((_, i) => (
                            <div
                                key={i}
                                className={`h-1.5 rounded-full transition-all ${
                                    i < currentIndex ? 'w-6 bg-emerald-500' :
                                    i === currentIndex ? 'w-8 bg-indigo-500' :
                                    'w-3 bg-slate-300 dark:bg-slate-700'
                                }`}
                            />
                        ))}
                        <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                            {currentIndex + 1} of {total}
                        </span>
                    </div>
                </div>

                {/* Body */}
                <div className="px-6 py-5 overflow-y-auto flex-1 space-y-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                    {current.body}
                </div>

                {/* Checkbox + Actions */}
                <div className="px-6 py-5 border-t border-slate-200 dark:border-slate-800 space-y-4 bg-slate-50 dark:bg-slate-950/50">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox
                            checked={agreed}
                            onCheckedChange={(v) => setAgreed(Boolean(v))}
                            className="mt-0.5"
                            data-testid="disclaimer-checkbox"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">
                            {current.checkboxLabel}
                        </span>
                    </label>

                    <div className="flex gap-2 justify-end flex-wrap">
                        {current.showExitButton && (
                            <Button
                                variant="ghost"
                                onClick={onExit}
                                data-testid="disclaimer-exit-btn"
                            >
                                Exit App
                            </Button>
                        )}
                        <Button
                            onClick={onContinue}
                            disabled={!agreed || submitting}
                            className="bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700"
                            data-testid="disclaimer-continue-btn"
                        >
                            {submitting ? 'Saving…' : currentIndex + 1 === total ? 'Finish' : 'Continue'}
                            <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
